import React, { useState, useEffect, useCallback } from 'react';
import tillService from '../services/tillService';
import storeService from '../services/storeService';
import { useStore } from '../context/StoreContext';
import OpenTillModal from '../components/till/OpenTillModal';
import CloseTillModal from '../components/till/CloseTillModal';
import ZReportModal from '../components/till/ZReportModal';
import CashierQuickActions from '../components/common/CashierQuickActions';
import { formatInStoreTimezone } from '../utils/dateUtils';
import useStoreTimezone from '../hooks/useStoreTimezone';
import { getPreferredPrinterConfig } from '../utils/printerPreferences';

const TillPage = () => {
  const [tills, setTills] = useState([]);
  const [selectedTill, setSelectedTill] = useState(null);
  const [isOpentillModalOpen, setIsOpenTillModalOpen] = useState(false);
  const [isCloseTillModalOpen, setIsCloseTillModalOpen] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [storeInfo, setStoreInfo] = useState(null);
  const { activeStore } = useStore();
  const timezone = useStoreTimezone();
  const [zReportSession, setZReportSession] = useState(null);

  const fetchTills = useCallback(async () => {
    if (activeStore) {
      try {
        const storeDetails = await storeService.getStoreById(activeStore._id);
        setTills(storeDetails.tills);
        setStoreInfo(storeDetails);
      } catch (err) {
        setError('Failed to fetch tills.');
      }
    }
  }, [activeStore]);

  useEffect(() => {
    fetchTills();
  }, [fetchTills]);

  const handleOpenTill = (till) => {
    setSelectedTill(till);
    setIsOpenTillModalOpen(true);
    triggerDrawerKick();
  };

  const handleConfirmOpenTill = async (openingFloat, openingDenominations) => {
    try {
      await tillService.openTillSession(selectedTill._id, openingFloat, openingDenominations);
      setSuccess('Till session opened successfully!');
      setIsOpenTillModalOpen(false);
      fetchTills(); // Refresh the tills
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to open till session.');
    }
  };

  const handleCloseTill = (till) => {
    setSelectedTill(till);
    setIsCloseTillModalOpen(true);
    triggerDrawerKick();
  };

  const handleConfirmCloseTill = async (closingFloat, closingDenominations, leaveAmount, closingNote) => {
    try {
      const session = await tillService.closeTillSession(
        selectedTill._id,
        closingFloat,
        closingDenominations,
        leaveAmount,
        closingNote
      );
      setSuccess('Till session closed successfully!');
      setIsCloseTillModalOpen(false);
      setZReportSession(session);
      fetchTills(); // Refresh the tills
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to close till session.');
    }
  };

  const handleViewZReport = (session) => {
    setZReportSession(session);
  };

  const getLatestClosedSession = (till) => {
    const closedSessions = (till?.sessions || []).filter((session) => session.status === 'Closed');
    if (!closedSessions.length) return null;
    const sorted = [...closedSessions].sort((a, b) => {
      const aTime = new Date(a?.closingTime || a?.openingTime || 0).getTime();
      const bTime = new Date(b?.closingTime || b?.openingTime || 0).getTime();
      return bTime - aTime;
    });
    return sorted[0];
  };

  const triggerDrawerKick = async () => {
    try {
      const plugin = window?.Capacitor?.Plugins?.ReceiptPrinter;
      if (!plugin) {
        console.debug('[TillPage] ReceiptPrinter plugin not available; cannot open drawer.');
        return;
      }
      const payload = { jobType: 'cash' };
      const cfg = getPreferredPrinterConfig('receipt');
      if (cfg.transport === 'network' && cfg.host) {
        payload.networkHost = cfg.host;
        if (cfg.port) {
          payload.networkPort = cfg.port;
        }
      } else if (cfg.bluetoothName) {
        payload.bluetoothNameHint = cfg.bluetoothName;
      }
      await plugin.openDrawer(payload);
    } catch (err) {
      console.warn('[TillPage] Drawer kick failed (non-blocking):', err);
    }
  };

  return (
    <div className="p-6">
      <CashierQuickActions />
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Till Management</h1>
      {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}
      {success && <p className="bg-green-100 text-green-700 p-3 rounded mb-4">{success}</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tills.map(till => (
          <div key={till._id} className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-bold mb-2">{till.name}</h2>
            <p>Status: {till.sessions.find(s => s.status === 'Open') ? 'Open' : 'Closed'}</p>
            <div className="mt-4 flex space-x-4">
              <button
                onClick={() => handleOpenTill(till)}
                disabled={till.sessions.find(s => s.status === 'Open')}
                className="bg-green-500 text-white px-4 py-2 rounded-md disabled:bg-gray-400"
              >
                Open
              </button>
              <button
                onClick={() => handleCloseTill(till)}
                disabled={!till.sessions.find(s => s.status === 'Open')}
                className="bg-red-500 text-white px-4 py-2 rounded-md disabled:bg-gray-400"
              >
                Close
              </button>
            </div>
            <div className="mt-4">
              <h3 className="text-lg font-bold">Recent Sessions</h3>
              <ul>
                {till.sessions.slice(0, 5).map(session => (
                  <li key={session._id}>
                    <button
                      onClick={() => handleViewZReport(session)}
                      className="text-blue-500 hover:underline"
                    >
                      {formatInStoreTimezone(session.openingTime, 'dd-MM-yyyy HH:mm', timezone)}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      {isOpentillModalOpen && (
        <OpenTillModal
          onConfirm={handleConfirmOpenTill}
          onCancel={() => setIsOpenTillModalOpen(false)}
          allowDirectEntry={Boolean(storeInfo?.tillOpeningDirectEntryEnabled)}
          lastClosedSession={getLatestClosedSession(selectedTill)}
          timezone={timezone}
        />
      )}

      {isCloseTillModalOpen && (
        <CloseTillModal
          onConfirm={handleConfirmCloseTill}
          onCancel={() => setIsCloseTillModalOpen(false)}
          openingFloat={selectedTill?.sessions?.find((session) => session.status === 'Open')?.openingFloat}
          openSession={selectedTill?.sessions?.find((session) => session.status === 'Open')}
        />
      )}

      {zReportSession && (
        <ZReportModal
          session={zReportSession}
          timezone={timezone}
          onClose={() => setZReportSession(null)}
        />
      )}
    </div>
  );
};

export default TillPage;
